import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
    Badge,
    Box,
    Button,
    Divider,
    Heading,
    Flex,
    HStack,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import Step1BasicDetails from './Step1BasicDetails';
import Step2Options from './Step2Options';
import Step3AdvancedOptions from './Step3AdvancedOptions';
import Step5ReviewSave from './Step5ReviewSave';
import {
    createProduct,
    duplicateProduct,
    showDuplicateProduct,
    showProduct,
    updateProduct,
} from '../../../api/productsApi';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';

const ProductForm = () => {
    const { t } = useTranslation()
    const tr = (key, defaultValue) => t(key, { defaultValue })
    const { id } = useParams();
    const location = useLocation();
    const isDuplicate = location.pathname.includes("duplicate");
    const navigate = useNavigate();
    const { addNotification, addErrorNotifications } = useNotification();

    const [currentStep, setCurrentStep] = useState(0);
    const [name, setName] = useState('');
    const [link, setLink] = useState('');
    const [slug, setSlug] = useState('');
    const [productId, setProductId] = useState('');
    const [options, setOptions] = useState([{ name: '', value: '', quantity: '', catch_phrase: '', image: null, imageUrl: '', reduced_price: '', colors: [{ color: '', woocommerce_product_id: '' }], productIds: [''], preselected: false, free_shipping: false }]);
    const [variations, setVariations] = useState([{ size: '', color: '', image: null, imageUrl: '' }]);
    const [removedImages, setRemovedImages] = useState([]);
    const [formId, setFormId] = useState(null);
    const [shouldSyncWithWooCommerce, setShouldSyncWithWooCommerce] = useState(false);
    const [showData, setShowData] = useState(true);
    const [useAdvancedOptions, setUseAdvancedOptions] = useState(false);
    const [useOptionId, setUseOptionId] = useState(false);
    const [useSummary, setUseSummary] = useState(false);
    const cardBg = useColorModeValue('white', 'navy.800');
    const pageBg = useColorModeValue('gray.50', 'navy.900');
    const mutedText = useColorModeValue('gray.500', 'gray.400');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
    const headingColor = useColorModeValue('gray.800', 'white');
    const stepTitleColor = useColorModeValue('gray.700', 'gray.200');
    const activeStepBg = useColorModeValue('blue.50', 'whiteAlpha.100');
    const activeStepTextColor = useColorModeValue('blue.700', 'blue.300');
    const inactiveCircleBg = useColorModeValue('gray.100', 'whiteAlpha.200');
    const inactiveCircleColor = useColorModeValue('gray.500', 'gray.300');
    const productSteps = [
        { title: tr('productForm.steps.basics.title', 'Basics'), description: tr('productForm.steps.basics.description', 'Name, IDs and product behavior') },
        { title: tr('productForm.steps.options.title', 'Options'), description: tr('productForm.steps.options.description', 'Prices, quantities and images') },
        { title: tr('productForm.steps.advanced.title', 'Advanced'), description: tr('productForm.steps.advanced.description', 'Colors and WooCommerce IDs') },
        { title: tr('productForm.steps.review.title', 'Review'), description: tr('productForm.steps.review.description', 'Confirm before saving') },
    ]
    const activeStep = productSteps[currentStep];
    const formMode = isDuplicate
        ? tr('productForm.mode.duplicate', 'Duplicate product')
        : id
            ? tr('productForm.mode.edit', 'Edit product')
            : tr('productForm.mode.create', 'Create product');

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                let product;
                if (isDuplicate) {
                    product = await showDuplicateProduct(id);
                    setFormId(product.form?.id);
                } else if (id) {
                    product = await showProduct(id);
                }

                if (product) {
                    if (!isDuplicate) {
                        setName(product.name);
                        setLink(product.link || '');
                        setSlug(product.slug);
                        setProductId(product.product_id);
                    } else {
                        setLink(product.link || '');
                    }
                    setShouldSyncWithWooCommerce(product.should_sync_with_woocommerce);
                    setShowData(product.show_data);
                    setUseAdvancedOptions(product.use_advanced_options);
                    setUseOptionId(product.use_option_id);
                    setUseSummary(product.use_summary);

                    const processedOptions = product.options.map(option => ({
                        ...option,
                        imageUrl: option.image_url || '',
                        colors: option.colors ? option.colors.map(color => ({
                            id: color.id || '',
                            color: color.color || '',
                            woocommerce_product_id: color.woocommerce_product_id || ''
                        })) : [{ color: '', woocommerce_product_id: '' }],
                        productIds: option.metadata?.product_ids || [''],
                        preselected: option.metadata?.preselected,
                        free_shipping: option.free_shipping,
                    }));

                    if (processedOptions.some(option => Array.isArray(option.productIds) && option.productIds.length > 0 && option.productIds[0] !== '')) setUseOptionId(true);

                    setOptions(processedOptions);

                    setVariations(product.variations.map(variation => ({
                        ...variation,
                        imageUrl: variation.image_url
                    })));
                }
            } catch (error) {
                console.error("Error fetching product:", error);
            }
        };

        fetchProduct();
    }, [id, location.pathname, isDuplicate]);

    const handleNextStep = () => setCurrentStep((prevStep) => Math.min(prevStep + 1, productSteps.length - 1));
    const handlePreviousStep = () => setCurrentStep((prevStep) => Math.max(prevStep - 1, 0));

    const handleSubmit = async () => {
        const filteredVariations = variations.filter(variation => {
            return variation.size || variation.color || variation.image
        }).map(variation => {
            const filteredVariation = { ...variation }
            if (!filteredVariation.size) {
                delete filteredVariation.size
            }
            if (!filteredVariation.color) {
                delete filteredVariation.color
            }
            return filteredVariation
        })

        const filteredOptions = options.filter(option => {
            return option.name || option.value || option.quantity || option.catch_phrase || option.image
        }).map(option => {
            const filteredOption = { ...option }
            if (!filteredOption.catch_phrase) {
                delete filteredOption.catch_phrase
            }
            if (!filteredOption.reduced_price) {
                delete filteredOption.reduced_price
            }
            if (useOptionId) {
                filteredOption.productIds = option.productIds.filter(pid => pid)
            }
            if (useAdvancedOptions) {
                filteredOption.colors = filteredOption.colors?.filter(color => {
                    return color.color && color.woocommerce_product_id
                }).map(color => {
                    const filteredColor = { ...color }
                    if (!filteredColor.id) {
                        delete filteredColor.id
                    }
                    return filteredColor
                })
            }
            return filteredOption
        })

        const productData = {
            name,
            product_id: productId,
            link,
            options: filteredOptions,
            variations: filteredVariations,
            removedImages,
            should_sync_with_woocommerce: shouldSyncWithWooCommerce,
            use_advanced_options: useAdvancedOptions,
            show_data: showData,
            use_option_id: useOptionId,
            use_summary: useSummary,
        };

        try {
            if (isDuplicate) {
                await duplicateProduct(id, { ...productData, form_id: formId });
                addNotification(tr('productForm.messages.duplicated', 'Product duplicated successfully'), 'success');
            } else if (id) {
                await updateProduct(id, productData);
                addNotification(tr('productForm.messages.updated', 'Product updated successfully'), 'success');
            } else {
                await createProduct(productData);
                addNotification(tr('productForm.messages.created', 'Product created successfully'), 'success');
            }
            navigate('/admin/products');
        } catch (error) {
            addErrorNotifications(error.response?.data);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <Step1BasicDetails
                        name={name}
                        setName={setName}
                        link={link}
                        setLink={setLink}
                        productId={productId}
                        setProductId={setProductId}
                        shouldSyncWithWooCommerce={shouldSyncWithWooCommerce}
                        setShouldSyncWithWooCommerce={setShouldSyncWithWooCommerce}
                        showData={showData}
                        setShowData={setShowData}
                        setUseAdvancedOptions={setUseAdvancedOptions}
                        useAdvancedOptions={useAdvancedOptions}
                        slug={slug}
                        useOptionId={useOptionId}
                        setUseOptionId={setUseOptionId}
                        useSummary={useSummary}
                        setUseSummary={setUseSummary}
                    />
                );
            case 1:
                return (
                    <Step2Options
                        options={options}
                        setOptions={setOptions}
                        useOptionId={useOptionId}
                    />
                );
            case 2:
                return (
                    <Step3AdvancedOptions
                        options={options}
                        setOptions={setOptions}
                    />
                );
            case 3:
                return (
                    <Step5ReviewSave
                        name={name}
                        slug={slug}
                        productId={productId}
                        shouldSyncWithWooCommerce={shouldSyncWithWooCommerce}
                        showData={showData}
                        useAdvancedOptions={useAdvancedOptions}
                        useOptionId={useOptionId}
                        options={options}
                        useSummary={useSummary}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Box
            bg={pageBg}
            maxWidth="xxl"
            px={{ base: '20px', md: '40px' }}
            py={{ base: '120px', md: '80px' }}
            minH="100vh"
        >
            <Flex mb="6" direction={{ base: 'column', md: 'row' }} justify="space-between" gap="4">
                <Box>
                    <Badge colorScheme="blue" mb="3" px="3" py="1" borderRadius="full">
                        {formMode}
                    </Badge>
                    <Text color={mutedText} mt="2">
                        {tr('productForm.description', 'Keep product details, buying options and integrations organized in one flow.')}
                    </Text>
                </Box>
                <HStack align="center" spacing="3">
                    <Badge colorScheme="purple" px="3" py="1" borderRadius="full">
                        {tr('productForm.step', 'Step')} {currentStep + 1} {tr('productForm.of', 'of')} {productSteps.length}
                    </Badge>
                </HStack>
            </Flex>

            <Box
                display="grid"
                gridTemplateColumns={{ base: '1fr', xl: '280px minmax(0, 1fr)' }}
                gap="6"
                alignItems="start"
            >
                <Box
                    bg={cardBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="2xl"
                    boxShadow="sm"
                    p="4"
                    position={{ xl: 'sticky' }}
                    top={{ xl: '24px' }}
                >
                    <Text fontSize="sm" fontWeight="700" color={stepTitleColor} mb="4">
                        {tr('productForm.setupProgress', 'Setup progress')}
                    </Text>
                    <Flex direction="column" gap="3">
                        {productSteps.map((step, index) => {
                            const isActive = currentStep === index;
                            const isComplete = currentStep > index;

                            return (
                                <Button
                                    key={step.title}
                                    type="button"
                                    variant="ghost"
                                    justifyContent="flex-start"
                                    h="auto"
                                    py="3"
                                    px="3"
                                    borderRadius="xl"
                                    bg={isActive ? activeStepBg : 'transparent'}
                                    borderWidth="1px"
                                    borderColor={isActive ? 'blue.200' : 'transparent'}
                                    onClick={() => setCurrentStep(index)}
                                >
                                    <HStack align="flex-start" spacing="3" w="100%">
                                        <Flex
                                            align="center"
                                            justify="center"
                                            boxSize="28px"
                                            borderRadius="full"
                                            bg={isActive || isComplete ? 'blue.500' : inactiveCircleBg}
                                            color={isActive || isComplete ? 'white' : inactiveCircleColor}
                                            fontSize="sm"
                                            flexShrink={0}
                                        >
                                            {index + 1}
                                        </Flex>
                                        <Box textAlign="left" minW="0">
                                            <Text fontWeight="700" color={isActive ? activeStepTextColor : stepTitleColor}>
                                                {step.title}
                                            </Text>
                                            <Text fontSize="xs" color={mutedText} whiteSpace="normal">
                                                {step.description}
                                            </Text>
                                        </Box>
                                    </HStack>
                                </Button>
                            );
                        })}
                    </Flex>
                </Box>

                <Box
                    bg={cardBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="2xl"
                    boxShadow="md"
                    overflow="hidden"
                >
                    <Box px={{ base: 5, md: 8 }} py="6" borderBottomWidth="1px" borderColor={borderColor}>
                        <Text color={mutedText} fontSize="sm" fontWeight="700" textTransform="uppercase">
                            {activeStep.title}
                        </Text>
                        <Heading size="md" mt="1" color={headingColor}>
                            {activeStep.description}
                        </Heading>
                    </Box>
                    <Box px={{ base: 5, md: 8 }} py="8">
                        {renderStep()}
                    </Box>
                    <Divider borderColor={borderColor} />
                    <Flex
                        justifyContent="space-between"
                        px={{ base: 5, md: 8 }}
                        py="5"
                        direction={{ base: 'column', md: 'row' }}
                        gap={4}
                    >
                        <Button
                            onClick={handlePreviousStep}
                            isDisabled={currentStep === 0}
                            variant="outline"
                            w={{ base: '100%', md: 'auto' }}
                        >
                            {tr('productForm.previous', 'Previous')}
                        </Button>
                        <Flex gap={4} direction={{ base: 'column', sm: 'row' }}>
                            <Button onClick={handleSubmit} colorScheme="teal" w={{ base: '100%', md: 'auto' }}>
                                {tr('productForm.save', 'Save')}
                            </Button>
                            {currentStep < productSteps.length - 1 && (
                                <Button onClick={handleNextStep} colorScheme="blue" w={{ base: '100%', md: 'auto' }}>
                                    {tr('productForm.next', 'Next')}
                                </Button>
                            )}
                        </Flex>
                    </Flex>
                </Box>
            </Box>
        </Box>
    );
};

export default ProductForm;